// 編成：出撃キャラの選択と、キャラごとの立ち絵＋名前・戦力・能力値、［能力値］［装備］［スキル］。キャラごとに持つものだけをここに置く。
// 装備のタブは立ち絵の下の帯に、そのキャラが持って出るものをまとめる：1段目に装備3枠（武器・防具・アクセサリー）と道具2枠、2段目にお守り（1人1つ）と式神。
// 枠を押すと下から出る札で選ぶ（付け替え・ロック・売却）。強化・限界突破・お守りの錬成・一括売却は強化の画面。
// 立ち絵は枠に入れず画面いっぱいに置き、名前・能力値と操作の札をその上に重ねる。能力値と装備のタブでは立ち絵を動かす（素材のあるキャラだけ）。
import {
  BATTLE_CONFIG,
  ITEM_BASE_COUNT,
  OMAMORI,
  battleItemsOf,
  omamoriMainText,
  styleOf,
  SHIKIGAMI_IDS,
  companionOf,
  CHARS,
  CHAR_IDS,
  EQUIPMENT,
  SKILL_BY_ID,
  SKILL_GROUPS,
  SKILL_GROUP_LABEL,
  equippedSkills,
  omamoriOwner,
  omamoriScore,
  skillCapacity,
  skillLines,
  skillCost,
  jutsuView,
  BUILD_STYLES,
  type CharId,
  type EquipItem,
  type EquipSlot,
  type OmamoriItem,
  type Profile,
} from '@hyakki/core';
import { api } from '../api';
import { battleItemArt, shikiArtHtml, faceStyle, hasFailed, isLoaded, loadSheet, portraitHtml, portraitKey } from '../assets';
import { localLoadout, powerOf } from '../core-bridge';
import { go, register } from '../router';
import { act, hasEnt, state, subscribe } from '../state';
import { esc, fmt, onAct } from '../ui/dom';
import { onSwipe, stepChar } from '../ui/fit';
import { icon } from '../ui/icons';
import { setLeader } from '../ui/leader';
import { confirmDialog, openModal, toast } from '../ui/overlay';
import { unseenUnlocks } from '../ui/skill-news';
import { errorText, notReadyHtml, runOp as run } from '../ui/status';
import { bindCompanionActions } from '../ui/companion';
import { GROUP_ICON, autoSkills, bindSkillLines, drawSkillLinesIn, jutsuArt, skillIcon, skillInfoDialog } from './skills';
import { bindItemsActions } from '../ui/items';
import { setPointsChar, setSubTab } from '../ui/shell';
import { artInfoDialog, artSummary, ougiSummary, surgeInfoDialog, surgeSummary } from '../ui/char-art';
import { SLOT_ICON, SLOT_LABEL, byRare, itemCellHtml, itemDetailHtml, itemName, itemThumb, runItemAction } from '../ui/equip-view';
import { omDetailHtml, omName, omThumbHtml, subChipsHtml } from '../ui/omamori-view';
import { LivePortrait, livePortrait } from '../ui/live-portrait';

export { RARITY, itemThumb } from '../ui/equip-view';

const TABS = [
  { id: 'stats', label: '能力値', icon: 'info' },
  { id: 'equip', label: '装備', icon: 'equip' },
  { id: 'skill', label: 'スキル', icon: 'sp' },
] as const;
type Tab = (typeof TABS)[number]['id'];
/** 立ち絵を見せるタブ */
const FIGURE_TABS: readonly Tab[] = ['stats', 'equip'];

let tab: Tab = 'stats';
let charId: CharId = 'akari';

register('chara', {
  title: '編成',
  mount(el, params) {
    const c = params.get('c') as CharId | null;
    charId = c && CHAR_IDS.includes(c) ? c : state.oshi;
    // 古いリンクの「式神」「道具」「式神・道具」（kit）は装備のタブへ（帯に式神・道具の枠がある）
    const t = ['kit', 'items', 'shiki'].includes(params.get('tab') ?? '') ? 'equip' : params.get('tab');
    if (t && TABS.some((x) => x.id === t)) tab = t as Tab;
    // 層：背景 → 立ち絵（動く立ち絵）→ 名前・能力値・キャラ切替 → 操作の札
    el.innerHTML = `<div class="chara">
      <div class="ch-bg" aria-hidden="true"></div>
      <div class="ch-figure" data-swipe-char></div>
      <header class="ch-hud"></header>
      <div class="ch-pick" role="radiogroup" aria-label="キャラ"></div>
      <section class="ch-panel panel-paper">
        <div class="seg-tabs ch-tabs" role="tablist">${TABS.map((x) => `<button role="tab" class="tab-btn" data-act="tab" data-tab="${x.id}">${icon(x.icon)}${x.label}<em class="badge" hidden></em></button>`).join('')}</div>
        <div class="tab-body"></div>
      </section></div>`;
    const root = el.querySelector<HTMLElement>('.chara')!;
    const figure = el.querySelector<HTMLElement>('.ch-figure')!;
    const hud = el.querySelector<HTMLElement>('.ch-hud')!;
    const pickEl = el.querySelector<HTMLElement>('.ch-pick')!;
    const body = el.querySelector<HTMLElement>('.tab-body')!;
    let alive = true;
    let lastFigure = '';
    /** 動く立ち絵を付けたキャラ（付いている間は静止画に差し替えない） */
    let liveFor: CharId | null = null;
    const draw = () => {
      if (!alive) return;
      const p = state.profile;
      setPointsChar(charId);
      // 立ち絵は中身が変わったときだけ差し替える（出だしの動きを繰り返さない）
      const showFigure = FIGURE_TABS.includes(tab);
      if (liveFor !== charId || !figure.querySelector('canvas')) {
        const fig = portraitHtml(charId, 'ch-img');
        if (fig !== lastFigure) figure.innerHTML = lastFigure = fig;
        if (showFigure) mountLive();
      }
      hud.innerHTML = hudHtml(p);
      pickEl.innerHTML = pickHtml();
      const fresh = p ? unseenUnlocks(p.level).length : 0;
      el.querySelectorAll<HTMLElement>('[data-tab]').forEach((b) => {
        b.classList.toggle('on', b.dataset.tab === tab);
        b.setAttribute('aria-selected', String(b.dataset.tab === tab));
        const badge = b.querySelector<HTMLElement>('.badge')!;
        badge.hidden = !(b.dataset.tab === 'skill' && fresh);
        badge.textContent = String(fresh);
      });
      const lines = tab === 'skill' && !!skillLines(charId);
      body.className = `tab-body tb-${tab}${lines ? ' tb-lines' : ''}`;
      root.dataset.tab = tab;
      root.classList.toggle('ch-lines', lines);
      setSubTab('chara', tab);
      // 能力値・装備のタブは札が中身の高さ：キャラ切替をその上に置くため高さを渡す
      requestAnimationFrame(() => {
        const panel = root.querySelector<HTMLElement>('.ch-panel');
        if (panel) root.style.setProperty('--ch-panel-h', `${panel.offsetHeight}px`);
      });
      if (!p) return void (body.innerHTML = notReadyHtml('育成データ'));
      if (tab === 'stats') drawStats(body, p);
      else if (tab === 'equip') drawLoadout(body, p);
      else if (skillLines(charId)) drawSkillLinesIn(body, p, charId);
      else drawSkillSummary(body, p);
    };
    // 動く立ち絵（素材のあるキャラだけ）：読み込めたら静止画と入れ替える。キャラを変えたら静止画に戻して読み直す
    const mountLive = () => {
      const id = charId;
      if (!LivePortrait.available(id)) return;
      void livePortrait(id).then((lp) => {
        if (!alive || !lp || charId !== id || !FIGURE_TABS.includes(tab)) return;
        lp.attach(figure, CHARS[id].name);
        liveFor = id;
        lastFigure = '';
      });
    };
    // 立ち絵：まだなら読んでから描き直す（無ければちびキャラの拡大）
    const wantPortrait = () => {
      const key = portraitKey(charId);
      if (!isLoaded(key) && !hasFailed(key)) void loadSheet(key).then(() => draw());
    };
    const pick = (id: CharId) => {
      if (id === charId) return;
      charId = id;
      liveFor = null;
      wantPortrait();
      draw();
    };
    draw();
    wantPortrait();
    const off = subscribe((ch) => {
      if (ch.has('profile') || ch.has('resources') || ch.has('oshi')) draw();
    });
    const offAct = onAct(el, {
      tab: (b) => {
        tab = b.dataset.tab as Tab;
        draw();
      },
      pick: (b) => pick(b.dataset.id as CharId),
      leader: () => void setLeader(charId),
      build: () => openBuildSheet(() => draw()),
      art: () => artInfoDialog(charId, state.profile ? localLoadout(state.profile, charId) : null),
      surge: () => surgeInfoDialog(charId, state.profile ? localLoadout(state.profile, charId) : null),
      tree: () => go('skills', { c: charId }),
      eqslot: (b) => openEquipPicker(b.dataset.slot as EquipSlot),
      shikigo: () => go('enhance', { tab: 'shiki' }),
      recommend: async (b) => {
        (b as HTMLButtonElement).disabled = true;
        await recommend();
        if (b.isConnected) (b as HTMLButtonElement).disabled = false;
      },
      omslot: (b) => openOmamoriPicker(Number(b.dataset.slot) || 0),
    });
    const offItems = bindItemsActions(el, () => draw()); // 道具（変更は下から出る札）
    const handler = makeHandler(() => draw());
    el.addEventListener('click', handler);
    const offComp = bindCompanionActions(el, () => draw()); // 加勢の式神（変更は下から出る札）
    const offLines = bindSkillLines(el, () => draw()); // 系統のキャラのスキル（＋−・技・術・おすすめ）
    // スキルの札を押す・マウスを乗せる：詳細（乗せたときは title の吹き出し）
    const skInfo = (e: Event) => {
      const ju = (e.target as HTMLElement).closest<HTMLElement>('.ch-jutsu [data-jutsu]');
      if (ju) return void pickJutsu(ju);
      const b = (e.target as HTMLElement).closest<HTMLElement>('[data-skinfo]');
      if (b) skillInfoDialog(b.dataset.skinfo!);
    };
    el.addEventListener('click', skInfo);
    const offSwipe = onSwipe(el, '[data-swipe-char]', (d) => pick(stepChar(charId, d)));
    return () => {
      alive = false;
      off();
      offAct();
      offSwipe();
      offItems();
      el.removeEventListener('click', handler);
      el.removeEventListener('click', skInfo);
      offLines();
      offComp();
    };
  },
});

/**
 * 能力値：能力（装備・スキル込み）と、術・妖力・奥義の札（押すと中身）。立ち絵を大きく見せるタブ
 */
function drawStats(body: HTMLElement, p: Profile) {
  const lo = localLoadout(p, charId);
  const s = lo.stats;
  const row = (k: string, v: string) => `<div><dt>${esc(k)}</dt><dd>${esc(v)}</dd></div>`;
  const art = artSummary(charId, lo);
  const sg = surgeSummary(charId, lo);
  const og = ougiSummary(charId, lo);
  body.innerHTML = `<div class="cs-sheet">
    <dl class="cs-kv">${row('HP', fmt(s.hp))}${row('攻撃', fmt(s.atk))}${row('防御', fmt(s.def))}${row('速さ', fmt(s.agi))}${row('移動', `+${BATTLE_CONFIG.moveBase + s.move}`)}${row('射程', String(s.range))}</dl>
    <div class="cs-cards">
      <button class="cs-card" data-act="art">${icon('skill')}<span><small>術</small><b>${esc(art.name)}</b><em>${esc(art.line)}</em></span>${icon('right', 'chev')}</button>
      ${og ? `<button class="cs-card ${og.on ? '' : 'off'}" data-act="art">${icon('sp')}<span><small>奥義</small><b>${esc(og.name)}</b><em>${esc(og.line)}</em></span>${icon('right', 'chev')}</button>` : ''}
      <button class="cs-card" data-act="surge">${icon('star')}<span><small>${esc(BATTLE_CONFIG.surge.label)}</small><b>${esc(sg.name)}</b>${sg.line ? `<em>${esc(sg.line)}</em>` : ''}</span>${icon('right', 'chev')}</button>
    </div>
  </div>`;
}

/**
 * 装備：立ち絵の下の帯。1段目＝装備3枠｜道具2枠、2段目＝お守り｜式神。見出しはどちらの段も枠の上の行
 * 押すと下から出る札で選ぶ（装備・お守りはこの画面の札、道具・式神は共通の札）
 */
function drawLoadout(body: HTMLElement, p: Profile) {
  const set = p.sets[charId] ?? { weapon: null, armor: null, netsuke: null };
  const byId = new Map(p.items.map((i) => [i.id, i]));
  const eq = (slot: EquipSlot) => {
    const x = set[slot] != null ? byId.get(set[slot]!) : null;
    return `<button class="lo-slot lo-eq" data-act="eqslot" data-slot="${slot}" aria-label="${SLOT_LABEL[slot]}${x ? `：${esc(itemName(x))}` : '（なし）'}">${x ? itemThumb(x) : `<span class="thumb empty">${icon(SLOT_ICON[slot])}</span>`}</button>`;
  };
  const items = battleItemsOf(p, charId);
  const counts = new Map((localLoadout(p, charId).items ?? []).map((x) => [x.id, x.count]));
  const item = (i: number) => {
    const id = items[i];
    const d = id ? BATTLE_CONFIG.items[id] : null;
    return `<button class="lo-slot lo-item" data-items-open data-items-char="${charId}" aria-label="道具${i + 1}${d ? `：${esc(d.name)}` : '（なし）'}">${
      id && d ? `<span class="thumb lo-it"><span class="ki-art" style="${battleItemArt(id)}"></span><em class="lo-n">×${counts.get(id) ?? ITEM_BASE_COUNT[id]}</em></span>` : `<span class="thumb empty">${icon('potion')}</span>`
    }</button>`;
  };
  // お守り：1人1つ。名前・主効果・副効果を札に（式神と並べる）
  const omId = omamoriSetOf(p, charId)[0];
  const o = omId != null ? p.omamori.find((x) => x.id === omId) : undefined;
  const om = `<button class="lo-slot lo-card lo-om" data-act="omslot" data-slot="0" aria-label="お守り${o ? `：${esc(omName(o.defId))}` : '（なし）'}">${
    o
      ? `${omThumbHtml(o, { size: 's' })}<span class="lo-card-t"><b>${esc(omName(o.defId))}</b><em>${esc(omamoriMainText(OMAMORI[o.defId], o.q))}</em>${subChipsHtml(o.subs)}</span>`
      : `<span class="thumb empty">${icon('omamori')}</span><span class="lo-card-t"><b>なし</b></span>`
  }</button>`;
  // 加勢の式神（戦いでカットインして技で助ける）。押すと選ぶ札
  const c = companionOf(p, charId);
  const anyShiki = SHIKIGAMI_IDS.some((id) => (p.shikigami?.[id]?.star ?? 0) > 0);
  const shiki = `<button class="lo-slot lo-card lo-shiki ${c ? '' : 'none'}" ${anyShiki ? `data-comp-open data-comp-char="${charId}"` : 'data-act="shikigo"'} aria-label="式神召喚${c ? `：${esc(c.name)}` : '（なし）'}">${
    c
      ? `<span class="lo-sk-face">${shikiArtHtml(c.enemyId, 44)}</span><span class="lo-card-t"><b>${esc(c.name)}</b><em>${'★'.repeat(c.star)}</em><i>Lv${c.level}</i></span>`
      : `<span class="thumb empty">${icon('shiki')}</span><span class="lo-card-t"><b>なし</b><em>${anyShiki ? '選ぶ' : '式神を集める'}</em></span>`
  }</button>`;
  body.innerHTML = `<div class="lo-band">
    <div class="lo-row lo-row1">
      <span class="lo-lb">${SLOT_LABEL.weapon}</span><span class="lo-lb">${SLOT_LABEL.armor}</span><span class="lo-lb">${SLOT_LABEL.netsuke}</span><span class="lo-lb lo-lb-items">道具</span>
      ${eq('weapon')}${eq('armor')}${eq('netsuke')}<i class="lo-sep" aria-hidden="true"></i>${item(0)}${item(1)}</div>
    <div class="lo-row lo-row2">
      <span class="lo-lb lo-lb-om">お守り</span><span class="lo-lb lo-lb-sk">召喚</span>
      ${om}<i class="lo-sep" aria-hidden="true"></i>${shiki}</div>
  </div>`;
}

/** そのキャラのお守りの枠（1人1つ） */
const omamoriSetOf = (p: Profile, c: CharId) => [p.omamoriSets?.[c]?.[0] ?? null];

/** 立ち絵の上に重ねる名前・戦力・出撃キャラ、能力値（装備・スキル込み）と固有の術 */
function hudHtml(p: Profile | null) {
  const c = CHARS[charId];
  const pw = powerOf(p, charId);
  const isLeader = charId === state.oshi;
  const s = p ? localLoadout(p, charId).stats : null;
  const row = (k: string, v: number) => `<span><small>${k}</small><b>${fmt(Math.round(v))}</b></span>`;
  return `<div class="ch-title"><h1>${esc(c.name)}</h1></div>
    <div class="ch-power"><span>戦力</span><b>${pw != null ? fmt(pw) : '—'}</b></div>
    ${tab === 'equip' ? `<button class="btn small primary ch-reco-btn" data-act="recommend">${icon('star')}おすすめ装備</button>` : ''}
    ${s && tab !== 'stats' && !(tab === 'skill' && skillLines(charId)) ? `<div class="ch-kv">${row('HP', s.hp)}${row('攻撃', s.atk)}${row('防御', s.def)}${row('速さ', s.agi)}${row('射程', s.range)}</div>` : ''}
    ${isLeader ? `<span class="ch-leader">${icon('sortie')}出撃キャラ</span>` : `<button class="btn small ch-leader-btn" data-act="leader">${icon('sortie')}出撃キャラにする</button>`}
    <button class="btn small ch-build-btn" data-act="build">${icon('star')}おたすけビルド${p?.skills?.[charId]?.style ? `<em>${esc(styleOf(charId, p.skills[charId].style)?.name ?? '')}</em>` : ''}</button>`;
}

/** キャラ切替（顔）。出撃キャラには印 */
function pickHtml() {
  return CHAR_IDS.map(
    (id) => `<button class="ch-face ${id === charId ? 'on' : ''}" role="radio" aria-checked="${id === charId}" data-act="pick" data-id="${id}" aria-label="${esc(CHARS[id].name)}"><span style="${faceStyle(id)}"></span>${id === state.oshi ? `<i>${icon('sortie')}</i>` : ''}</button>`,
  ).join('');
}

function makeHandler(redraw: () => void) {
  return async (e: Event) => {
    const t = e.target as HTMLElement;
    const p = state.profile;
    if (!p) return;
    const btn = t.closest<HTMLButtonElement>('button');
    if (btn?.disabled) return;
    const a = t.closest<HTMLElement>('[data-a]')?.dataset.a;
    if (!a || t.closest('.ln-view')) return;
    if (btn) btn.disabled = true;
    switch (a) {
      case 'skauto':
        await autoSkills(p, charId);
        break;
    }
    if (btn?.isConnected) btn.disabled = false;
  };
}

// ---------- 装備・お守りを選ぶ札（下から出る） ----------

/**
 * 装備を選ぶ札：その部位で、このキャラに付けられる装備を「付けたときの戦力の差」の順に。押すと下に詳細（装備・外す・ロック・売却・強化へ）
 */
function openEquipPicker(slot: EquipSlot) {
  if (!state.profile) return;
  let selId: number | null = state.profile.sets[charId]?.[slot] ?? null;
  const html = () => {
    const p = state.profile!;
    const set = p.sets[charId] ?? { weapon: null, armor: null, netsuke: null };
    // 他のキャラ専用の武器は、このキャラには付けられない
    const usable = (i: EquipItem) => !EQUIPMENT[i.defId]?.charId || EQUIPMENT[i.defId]?.charId === charId;
    const base = powerOf(p, charId) ?? 0;
    const gain = new Map<number, number>();
    const list = p.items.filter((i) => i.slot === slot && usable(i));
    for (const i of list) {
      const q = { ...p, sets: { ...p.sets, [charId]: { ...set, [slot]: i.id } } } as Profile;
      gain.set(i.id, (powerOf(q, charId) ?? 0) - base);
    }
    list.sort((a, b) => Number(b.id === set[slot]) - Number(a.id === set[slot]) || gain.get(b.id)! - gain.get(a.id)! || byRare(a, b));
    if (selId != null && !p.items.some((x) => x.id === selId)) selId = null;
    const it = selId != null ? p.items.find((x) => x.id === selId) : undefined;
    const tag = (i: EquipItem) => {
      if (i.id === set[slot]) return `<em class="pk-on">${icon('check')}</em>`;
      const g = Math.round(gain.get(i.id) ?? 0);
      return g ? `<em class="pk-gain ${g > 0 ? 'up' : 'down'}">${g > 0 ? '+' : ''}${fmt(g)}</em>` : '';
    };
    return `<div class="pk-head"><span class="muted small">所持 ${list.length}</span></div>
      <ul class="inv-grid pk-grid fit-scroll">${list.map((i) => itemCellHtml(p, i, { sel: i.id === selId, tag: tag(i) })).join('') || `<li class="muted small inv-empty">${SLOT_LABEL[slot]}がありません。合戦で手に入ります。</li>`}</ul>
      ${it ? `<div class="pk-detail">${itemDetailHtml(p, it, charId, 'loadout')}</div>` : ''}`;
  };
  const m = openModal(`<div class="pk-sheet">${html()}</div>`, { title: `${CHARS[charId].name}の${SLOT_LABEL[slot]}`, className: 'sheet-wrap pk-wrap' });
  const box = m.body.querySelector<HTMLElement>('.pk-sheet')!;
  const redraw = () => {
    if (!state.profile) return m.close();
    box.innerHTML = html();
  };
  const off = subscribe((ch) => {
    if (!m.el.isConnected) return off();
    if (ch.has('profile') || ch.has('resources')) redraw();
  });
  m.body.addEventListener('click', async (e) => {
    const t = e.target as HTMLElement;
    const cell = t.closest<HTMLElement>('[data-item]');
    if (cell) {
      selId = Number(cell.dataset.item);
      return redraw();
    }
    const b = t.closest<HTMLButtonElement>('[data-a]');
    const p = state.profile;
    const it = p?.items.find((x) => x.id === selId);
    if (!b || b.disabled || !p || !it) return;
    const a = b.dataset.a!;
    if (a === 'close') {
      selId = null;
      return redraw();
    }
    if (a === 'forge') {
      m.close();
      return go('enhance', { tab: 'equip', item: it.id });
    }
    b.disabled = true;
    if ((await runItemAction(a, p, it, charId)) && a === 'equip') m.close();
    if (b.isConnected) b.disabled = false;
  });
}

/**
 * お守りを選ぶ札：強い順。ほかのキャラが付けているものは顔の印（選ぶと付け替え）。押すと下に詳細（装備・外す・ロック・錬成へ）
 */
function openOmamoriPicker(slot: number) {
  if (!state.profile) return;
  let selId: number | null = omamoriSetOf(state.profile, charId)[slot];
  const html = () => {
    const p = state.profile!;
    const mine = omamoriSetOf(p, charId);
    const list = p.omamori.slice().sort((a, b) => Number(mine.includes(b.id)) - Number(mine.includes(a.id)) || omamoriScore(b) - omamoriScore(a) || b.id - a.id);
    if (selId != null && !p.omamori.some((x) => x.id === selId)) selId = null;
    const o = selId != null ? p.omamori.find((x) => x.id === selId) : undefined;
    const cell = (x: OmamoriItem) => {
      const by = omamoriOwner(p, x.id);
      return `<li><button class="om2-cell ${x.id === selId ? 'sel' : ''}" data-om="${x.id}">${omThumbHtml(x, { size: 's', equippedAt: by === charId ? mine.indexOf(x.id) : -1 })}
        <span class="om2-cell-t"><b>${esc(omName(x.defId))}</b>${subChipsHtml(x.subs)}</span>${by && by !== charId ? `<span class="by-face" style="${faceStyle(by)}" aria-label="${esc(CHARS[by].name)}が装備中"></span>` : ''}</button></li>`;
    };
    const at = o ? mine.indexOf(o.id) : -1;
    const by = o ? omamoriOwner(p, o.id) : null;
    return `<ul class="om2-grid pk-grid fit-scroll">${list.map(cell).join('') || '<li class="muted small inv-empty">まだありません。おまもりガチャ（毎日1回無料）・章ボスの初回撃破・星報酬・図鑑で手に入ります。</li>'}</ul>
      ${
        o
          ? `<div class="pk-detail om-detail">${omDetailHtml(o, { compact: true })}
        ${by && by !== charId ? `<p class="ed-note"><span class="by-face inline" style="${faceStyle(by)}"></span>${CHARS[by].name}が装備中</p>` : ''}
        <div class="ed-actions pk-acts">
          ${at >= 0 ? `<button class="btn" data-a="omremove">外す</button>` : `<button class="btn primary" data-a="omequip">装備</button>`}
          <button class="btn ${o.locked ? 'primary' : ''}" data-a="omlock" ${o.paid ? 'disabled' : ''}>${icon(o.locked ? 'lock' : 'unlock')}${o.locked ? 'ロック解除' : 'ロック'}</button>
          <button class="btn" data-a="omforge">${icon('enhance')}錬成</button>
        </div></div>`
          : ''
      }`;
  };
  const m = openModal(`<div class="pk-sheet">${html()}</div>`, { title: `${CHARS[charId].name}のお守り`, className: 'sheet-wrap pk-wrap' });
  const box = m.body.querySelector<HTMLElement>('.pk-sheet')!;
  const redraw = () => {
    if (!state.profile) return m.close();
    box.innerHTML = html();
  };
  const off = subscribe((ch) => {
    if (!m.el.isConnected) return off();
    if (ch.has('profile')) redraw();
  });
  m.body.addEventListener('click', async (e) => {
    const t = e.target as HTMLElement;
    const cell = t.closest<HTMLElement>('[data-om]');
    if (cell) {
      selId = Number(cell.dataset.om);
      return redraw();
    }
    const b = t.closest<HTMLButtonElement>('[data-a]');
    const p = state.profile;
    const o = p?.omamori.find((x) => x.id === selId);
    if (!b || b.disabled || !p || !o) return;
    const a = b.dataset.a!;
    if (a === 'omforge') {
      m.close();
      return go('enhance', { tab: 'omamori' });
    }
    b.disabled = true;
    if (a === 'omequip') {
      if (await run(api.equipOmamori(charId, slot, o.id), undefined, 'equip')) m.close();
    } else if (a === 'omremove') {
      await run(api.equipOmamori(charId, omamoriSetOf(p, charId).indexOf(o.id), null));
    } else if (a === 'omlock') {
      await run(api.lockOmamori(o.id, !o.locked), o.locked ? 'ロックを外しました' : 'ロックしました');
    }
    if (b.isConnected) b.disabled = false;
  });
}

/**
 * おすすめ装備（戦力優先）：装備3枠はサーバーの見積もり（自分の分だけの最良と、他のキャラの装備も使った最良を比べて提案）、
 * お守りはその装備で戦力が最も上がる1つ（他のキャラが付けているものは使わない）。まとめて確認して付け替える
 */
async function recommend() {
  const [own, all] = await Promise.all([api.recommend(charId, false, false), api.recommend(charId, true, false)]).catch((e) => {
    toast(errorText(e), 'error');
    return [null, null];
  });
  const p = state.profile;
  if (!own || !all || !p) return;
  const before = powerOf(p, charId) ?? 0;
  // 案ごとに：装備をその組にしたうえで、最も戦力が上がるお守り（他のキャラが付けているものは使わない）まで含めた最終の戦力
  const plan = (r: typeof own.recommendation, others: boolean) => {
    const eqChange = r.after > r.before;
    const withSet = { ...p, sets: { ...p.sets, [charId]: eqChange ? r.set : p.sets[charId] } } as Profile;
    const cur = omamoriSetOf(p, charId)[0];
    const withOm = (id: number | null) => ({ ...withSet, omamoriSets: { ...p.omamoriSets, [charId]: [id] } }) as Profile;
    let om = { id: cur, power: powerOf(withOm(cur), charId) ?? 0 };
    for (const it of p.omamori) {
      const by = omamoriOwner(p, it.id);
      if (by && by !== charId) continue;
      const pw = powerOf(withOm(it.id), charId) ?? 0;
      if (pw > om.power + 1e-9) om = { id: it.id, power: pw };
    }
    const omChange = om.id !== cur && om.id != null;
    return { others, eqChange, omChange, om, power: Math.round(om.power), who: [...new Set(r.takenFrom.map((t) => CHARS[t.charId].name))].join('・') };
  };
  const plans = [plan(own.recommendation, false)];
  if (all.recommendation.after > own.recommendation.after && all.recommendation.takenFrom.length) plans.push(plan(all.recommendation, true));
  const useful = plans.filter((x) => x.eqChange || x.omChange);
  if (!useful.length) return void toast('今の装備が、最も戦力が高い組み合わせです');
  // 選択肢を並べ、押した案でそのまま付け替える（確認は1回）
  const pick = await new Promise<(typeof plans)[number] | null>((resolve) => {
    let done = false;
    const m = openModal(
      `<p class="rc-now">今の戦力 <b>${fmt(before)}</b></p>
      <div class="rc-opts">${useful
        .map((x, i) => {
          const omLine = x.omChange ? `お守り：${esc(omName(p.omamori.find((y) => y.id === x.om.id)!.defId))}` : '';
          return `<button class="rc-opt" data-i="${i}"><span class="rc-t">${x.others ? '他のキャラの装備も使う' : '自分の装備だけ'}</span>
            <span class="rc-pw">${fmt(before)}<i>→</i><b>${fmt(x.power)}</b></span>
            ${x.others ? `<small>${esc(x.who)} から外して付け替えます</small>` : ''}${omLine ? `<small>${omLine}</small>` : ''}</button>`;
        })
        .join('')}</div>`,
      { title: 'おすすめ装備', onClose: () => !done && resolve(null) },
    );
    m.body.addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest<HTMLElement>('.rc-opt');
      if (!b) return;
      done = true;
      m.close();
      resolve(useful[Number(b.dataset.i)] ?? null);
    });
  });
  if (!pick) return;
  if (pick.eqChange && !(await run(api.recommend(charId, pick.others, true)))) return;
  if (pick.omChange && !(await run(api.equipOmamori(charId, 0, pick.om.id)))) return;
  toast(`おすすめ装備を付けました（戦力 ${fmt(powerOf(state.profile, charId) ?? pick.power)}）`, 'good');
}

// ---------- スキル（構成の要約。組み替えはスキルツリーのページ） ----------
function drawSkillSummary(body: HTMLElement, p: Profile) {
  const cur = equippedSkills(p, charId);
  const cap = skillCapacity(p).total;
  const used = skillCost(cur);
  const auto = p.skills?.[charId]?.auto !== false;
  const pct = Math.min(100, (used / Math.max(1, cap)) * 100);
  const fresh = unseenUnlocks(p.level).length;
  const groups = SKILL_GROUPS.map((g) => {
    const list = cur.map((id) => SKILL_BY_ID[id]).filter((s) => s && (g === 'char' ? s.charId === charId : s.group === g && s.charId === null));
    const label = g === 'char' ? `${CHARS[charId].name}専用` : SKILL_GROUP_LABEL[g];
    return `<li class="chs-group"><h3 class="sec-title">${icon(GROUP_ICON[g])}${esc(label)}<small>${list.length}</small></h3>
      <div class="chs-chips">${list.map((s) => `<button class="chs-chip" data-skinfo="${esc(s.id)}" title="${esc(s.name)}：${esc(s.desc)}">${icon(skillIcon(s))}${esc(s.name)}</button>`).join('') || '<span class="muted small">なし</span>'}</div></li>`;
  }).join('');
  body.innerHTML = `<div class="ch-skl">
    ${jutsuPicker(p)}
    <div class="skl-meter" role="meter" aria-label="ポイント" aria-valuemin="0" aria-valuemax="${cap}" aria-valuenow="${used}">
      <span class="skm-l">ポイント</span><span class="skm-v"><span class="skm-use">使用 </span><b>${used}</b> / ${cap}</span>${auto ? '<em class="tag skm-auto">おまかせ</em>' : ''}
      <span class="meter"><i style="width:${pct}%"></i></span>
    </div>
    <ul class="chs-list fit-scroll">${groups}</ul>
    <div class="chs-acts">
      <button class="btn big" data-a="skauto">${icon('star')}おすすめ構成</button>
      <button class="btn primary big" data-act="tree">${icon('sp')}スキルツリー${fresh ? `<em class="badge">${fresh}</em>` : ''}${icon('right', 'chev')}</button>
    </div></div>`;
}

/** 持っていく術：3つの札（術の絵・名前・効果）。覚えた術を押すと持っていく。覚えていなければ、覚えるスキルをツリーで開く */
function jutsuPicker(p: Profile): string {
  const list = jutsuView(p, charId);
  return `<div class="ch-jutsu" role="radiogroup" aria-label="セットする術">${list
    .map((j) => {
      const from = j.skill ? SKILL_BY_ID[j.skill] : null;
      return `<button class="chj ${j.selected ? 'on' : ''} ${j.learned ? '' : 'locked'}" role="radio" aria-checked="${j.selected}" aria-label="術「${esc(j.name)}」${j.learned ? '' : '（未習得）'}" data-jutsu="${esc(j.id)}" data-name="${esc(j.name)}" data-learned="${j.learned ? 1 : 0}" ${from ? `data-from="${esc(from.id)}"` : ''} title="${esc(j.desc)}">
        <span class="chj-art"><i style="${jutsuArt(j.id)}"></i>${j.learned ? '' : `<em class="chj-lock">${icon('lock')}</em>`}</span>
        <span class="chj-txt"><b>${esc(j.name)}</b><small class="chj-desc">${esc(j.learned ? j.desc : from ? `「${from.name}」で覚える` : j.how ? `${j.how}で覚える` : '未習得')}</small></span>${j.selected ? `<em class="chj-on">${icon('check')}</em>` : ''}</button>`;
    })
    .join('')}</div>`;
}

/** 術の札を押した：覚えていれば持っていく術にする、覚えていなければ覚えるスキルをツリーで開く */
async function pickJutsu(b: HTMLElement) {
  if (b.dataset.learned !== '1') return void (b.dataset.from ? go('skills', { c: charId, s: b.dataset.from }) : undefined);
  if (b.classList.contains('on')) return;
  await run(api.skillsJutsu(charId, b.dataset.jutsu!), `術「${b.dataset.name}」を持っていきます`, 'unlock');
}

/**
 * おすすめビルド（無料：標準＝戦力が最も高くなるように）／おたすけビルド（課金の権利 otasuke：型も選べる）：標準（おすすめ）と、そのキャラの3つの型（別セッションの型の検討・BALANCE.md 23章）から選ぶと、
 * スキル・装備・お守り・道具・式神をまとめて組む（サーバーで。スキルは以後も主Lvが上がるたびに型で組み直す）
 */
function openBuildSheet(redraw: () => void) {
  const p = state.profile;
  if (!p) return;
  const cur = p.skills?.[charId]?.auto ? (p.skills[charId].style ?? 'std') : null;
  const rows = [
    { key: 'std', name: '標準（おすすめ）', desc: 'いちばん戦力が高くなるように、スキル・装備・式神を組む' },
    ...BUILD_STYLES[charId],
  ]
    .map((s) => {
      return `<button class="bs-row ${cur === s.key ? 'on' : ''} ${s.key === 'std' ? 'free' : ''}" data-style="${s.key}">
        <span class="grow"><b>${esc(s.name)}${cur === s.key ? '<em class="bs-cur">今の型</em>' : ''}</b><small>${esc(s.desc)}</small></span>${icon('right', 'chev')}</button>`;
    })
    .join('');
  const owned = hasEnt('otasuke');
  // 装備の候補：自分の装備だけ（既定）か、他のキャラが付けているものも外して使うか。端末に覚える
  let others = false;
  try {
    others = localStorage.getItem('hk.buildOthers') === '1';
  } catch {
    /* 覚えられなくても選べる */
  }
  const seg = () =>
    `<div class="seg bs-src" role="radiogroup" aria-label="使う装備">${[false, true]
      .map((v) => `<button role="radio" class="chip ${v === others ? 'on' : ''}" aria-checked="${v === others}" data-others="${v ? 1 : 0}">${v ? '他のキャラの装備も使う' : '自分の装備だけ'}</button>`)
      .join('')}</div>`;
  const m = openModal(
    `${owned ? '' : `<p class="bs-lead">型を選べる「おたすけビルド」<button class="btn small gold" data-act="shop">${icon('lock')}ショップで解放</button></p>`}${seg()}<div class="bs-list ${owned ? '' : 'locked'}">${rows}</div>`,
    { title: `${CHARS[charId].name}の${owned ? 'おたすけビルド' : 'おすすめビルド'}`, className: 'sheet-wrap navy-sheet bs-sheet' },
  );
  m.body.addEventListener('click', async (e) => {
    if ((e.target as HTMLElement).closest('[data-act="shop"]')) {
      m.close();
      go('shop', { cat: 'perm', focus: 'otasuke', from: 'otasuke' });
      return;
    }
    const o = (e.target as HTMLElement).closest<HTMLElement>('[data-others]');
    if (o) {
      others = o.dataset.others === '1';
      try {
        localStorage.setItem('hk.buildOthers', others ? '1' : '0');
      } catch {
        /* 覚えられなくても選べる */
      }
      m.body.querySelector('.bs-src')!.outerHTML = seg();
      return;
    }
    const b = (e.target as HTMLButtonElement).closest<HTMLButtonElement>('[data-style]');
    if (!b || b.disabled) return;
    if (b.dataset.style !== 'std' && !hasEnt('otasuke')) {
      if (await confirmDialog('おたすけビルドは「おたすけビルド」の権利で使えます。ショップを開きますか？', { ok: 'ショップへ' })) {
        m.close();
        go('shop', { cat: 'perm', focus: 'otasuke', from: 'otasuke' });
      }
      return;
    }
    b.disabled = true;
    try {
      const r = await act(api.recommendBuild(charId, b.dataset.style!, others));
      toast(`${b.querySelector('b')?.firstChild?.textContent ?? ''}に組みました（戦力 ${fmt(r.before)} → ${fmt(r.after)}）`, 'good', 3200);
      m.close();
      redraw();
    } catch (err) {
      toast(errorText(err), 'error');
      b.disabled = false;
    }
  });
}
